import { create } from "zustand";
import { docBelongsToActiveWorkspace, getActiveDesignFirmId } from "ui";
import { FFEScheduleInterface, Permissions } from "ts-interfaces";
import { getFFESchedulesByProject } from "../../../v2/apis/ffeSchedulesApi";
import {
  activeTeamScope,
  awaitActiveTeamScope,
  teamScopeKnown,
} from "../../../v2/apis/accessQueries";
import { getCachedUserSchedules } from "../../../hooks/useUserSchedules";

/**
 * Shared project-schedules state, keyed by projectId, so the overview's three
 * consumers (sidebar badge, SchedulesPanel, the create action) read one fetch
 * and see the same archive instead of three independent useState+fetch copies.
 *
 * In-flight dedup, NOT a cache: concurrent load()s join one promise (3 -> 1
 * fetch), then the key clears so a later visit re-fetches (a cache would dedup
 * harder but go stale after an archive elsewhere).
 *
 * All Firestore access goes through ffeSchedulesApi (schedule Rule 1).
 */

export interface ProjectSchedulesEntry {
  schedules: FFEScheduleInterface[];
  loading: boolean;
  loadError: boolean;
}

interface ProjectSchedulesState {
  byProject: Record<string, ProjectSchedulesEntry>;
  load: (
    projectId: string | undefined,
    uid: string | undefined,
    permissions: Permissions | null,
  ) => Promise<void>;
  removeLocal: (projectId: string, scheduleId: string) => void;
  upsertLocal: (projectId: string, schedule: FFEScheduleInterface) => void;
}

const EMPTY: ProjectSchedulesEntry = {
  schedules: [],
  loading: false,
  loadError: false,
};

// Keyed by projectId + team scope — joined by concurrent callers, cleared once settled.
const inFlight = new Map<string, Promise<void>>();

const setEntry = (
  set: (
    fn: (s: ProjectSchedulesState) => Partial<ProjectSchedulesState>,
  ) => void,
  projectId: string,
  patch: Partial<ProjectSchedulesEntry>,
) =>
  set((s) => ({
    byProject: {
      ...s.byProject,
      [projectId]: { ...(s.byProject[projectId] ?? EMPTY), ...patch },
    },
  }));

export const useProjectSchedulesStore = create<ProjectSchedulesState>(
  (set, get) => ({
    byProject: {},

    load: async (projectId, uid, permissions) => {
      if (!projectId || !uid) {
        if (projectId) setEntry(set, projectId, { ...EMPTY });
        return;
      }
      // Teams sharing: the query shape follows the team's flag (read first on
      // a cold load); a load under the other shape is never joined.
      const teamScope = teamScopeKnown(uid)
        ? activeTeamScope(uid)
        : await awaitActiveTeamScope(uid);
      const key = `${projectId}|${teamScope?.firmId ?? ""}`;
      const existing = inFlight.get(key);
      if (existing) return existing;

      // First visit this session: paint instantly from the dashboard's
      // cross-project schedules cache so the panel doesn't spin on data it
      // already fetched. The request below still runs — it revalidates and
      // also covers schedules shared with the user that the cross-project
      // query can't see. Revisits keep their store entry and just revalidate.
      if (!get().byProject[projectId]) {
        const seed = (getCachedUserSchedules(uid) ?? []).filter(
          (s) => s.projectId === projectId,
        );
        setEntry(
          set,
          projectId,
          seed.length > 0
            ? { schedules: seed, loading: false, loadError: false }
            : { loading: true, loadError: false },
        );
      } else {
        setEntry(set, projectId, { loading: true, loadError: false });
      }
      const request = (async () => {
        try {
          const all = await getFFESchedulesByProject(
            projectId,
            uid,
            permissions,
            getActiveDesignFirmId(uid),
            teamScope,
          );
          // Option-B workspace filtering (docs/workspaces-architecture.md): the
          // query returns everything readable; filter to the active workspace here.
          setEntry(set, projectId, {
            schedules: all.filter((s) => docBelongsToActiveWorkspace(s, uid)),
            loading: false,
            loadError: false,
          });
        } catch (e: any) {
          console.error("Error loading project schedules:", e);
          // loadError so the panel shows an error, not the "create one" empty state.
          setEntry(set, projectId, {
            schedules: [],
            loading: false,
            loadError: true,
          });
        } finally {
          inFlight.delete(key);
        }
      })();
      inFlight.set(key, request);
      return request;
    },

    removeLocal: (projectId, scheduleId) => {
      const entry = get().byProject[projectId];
      if (!entry) return;
      setEntry(set, projectId, {
        schedules: entry.schedules.filter((s) => s.id !== scheduleId),
      });
    },

    // Insert a schedule created outside the panel (e.g. the onboarding flow's
    // background create) without a refetch. A load() whose Firestore query
    // started BEFORE the schedule was written can still be in flight and would
    // overwrite the insert with a snapshot that misses the doc — so while the
    // entry is loading, defer the insert until it settles.
    upsertLocal: (projectId, schedule) => {
      const insert = () => {
        const entry = get().byProject[projectId];
        if (entry?.schedules.some((s) => s.id === schedule.id)) return;
        setEntry(set, projectId, {
          schedules: [schedule, ...(entry?.schedules ?? [])],
          loading: false,
          loadError: false,
        });
      };
      if (!get().byProject[projectId]?.loading) {
        insert();
        return;
      }
      const settle = () => {
        clearTimeout(backstop);
        unsubscribe();
        insert();
      };
      const unsubscribe = useProjectSchedulesStore.subscribe((s) => {
        if (!s.byProject[projectId]?.loading) settle();
      });
      // Backstop: load() always settles `loading` today, but if a future
      // path ever leaves an entry loading, don't leak the subscription —
      // insert anyway (worst case: the pre-fix overwrite race, no worse).
      const backstop = setTimeout(settle, 15000);
    },
  }),
);

/** Test isolation: the store is a module singleton and outlives a test file. */
export const resetProjectSchedules = () => {
  inFlight.clear();
  useProjectSchedulesStore.setState({ byProject: {} });
};
